import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Dialog, DialogContent, Button, Grid, Box, Typography, Paper } from '@mui/material';
import { buildMatchAnalysis } from '../../utils/matchAnalysis.js';
import computePlayerRatingsForMatch from './playerRatingsShared';
import PlayerInsightContent from './PlayerInsightContent';
import { seekReplayEvidence } from '../../game2';

function formatRating(value) {
  return Number.isFinite(Number(value)) ? Number(value).toFixed(1) : '—';
}

function formatValue(entry) {
  if (entry.value === undefined || entry.value === null || !Number.isFinite(Number(entry.value))) return '—';
  const number = entry.decimals === undefined ? entry.value : Number(entry.value).toFixed(entry.decimals);
  return (entry.prefix || '') + number + (entry.suffix || '');
}

export default function RatingDialog({ open, onClose, initialPlayer = null }) {
  const matches = useSelector((state) => state.gameStats.matches) || [];
  const selectedMatch = useSelector((state) => state.gameStats.selectedMatch) || 0;
  const [selectedPlayer, setSelectedPlayer] = useState(initialPlayer);

  const match = matches[selectedMatch] || null;
  const analysis = useMemo(() => buildMatchAnalysis(match), [match]);
  const data = useMemo(() => computePlayerRatingsForMatch(match, analysis), [match, analysis]);

  useEffect(() => {
    if (open && initialPlayer) setSelectedPlayer(initialPlayer);
  }, [open, initialPlayer]);

  useEffect(() => {
    const players = [...(data.red || []), ...(data.blue || [])];
    if (!players.length) {
      setSelectedPlayer(null);
      return;
    }
    if (selectedPlayer && players.some((player) => player.nick === selectedPlayer)) return;
    setSelectedPlayer(players.find((player) => player.nick === initialPlayer)?.nick || players[0].nick);
  }, [data.red, data.blue, selectedPlayer, initialPlayer]);

  const selectedSummary = useMemo(() => {
    if (!analysis || !selectedPlayer) return null;
    return analysis.playerSummaries.find((summary) => summary.nick === selectedPlayer) || null;
  }, [analysis, selectedPlayer]);

  const selectedData = useMemo(() => {
    if (!selectedPlayer) return null;
    return [...(data.red || []), ...(data.blue || [])].find((player) => player.nick === selectedPlayer) || null;
  }, [data.red, data.blue, selectedPlayer]);

  const possessionContext = useMemo(() => {
    if (!analysis || !selectedPlayer || !Array.isArray(analysis.possessionTimeline)) return null;
    let scoring = 0;
    let turnovers = 0;
    let touches = 0;
    let starts = 0;
    analysis.possessionTimeline.forEach((possession) => {
      if (!possession || !possession.players || typeof possession.players.get !== 'function') return;
      const info = possession.players.get(selectedPlayer);
      if (!info) return;
      touches += info.touches || 0;
      if (possession.firstPlayer === selectedPlayer) starts += 1;
      if (possession.result === 'goal') scoring += 1;
      if (possession.result === 'turnover') turnovers += 1;
    });
    return { scoring, turnovers, touches, starts };
  }, [analysis, selectedPlayer]);

  const playerDetailEntries = useMemo(() => {
    if (!selectedSummary) return [];
    const metrics = selectedSummary.metrics || {};
    const detail = [
      { label: 'Starting rating', value: selectedData?.baseline ?? 6.5 },
      ...(selectedData?.breakdown?.contributions || []).map((item) => ({
        label: item.label + ' contribution',
        value: item.value,
        decimals: 3,
        contribution: true,
        prefix: item.value > 0 ? '+' : ''
      })),
      { label: 'Final Rating', value: selectedData?.rating, decimals: 1 },
      { label: 'Goals', value: metrics.goals },
      { label: 'Assists', value: metrics.assists },
      { label: 'xG', value: metrics.xg, decimals: 2 },
      { label: 'Shots on Target', value: metrics.shotsOnTarget },
      { label: 'Saves', value: metrics.saves },
      { label: 'Progressive Passes', value: metrics.progressivePasses },
      { label: 'Progressive Distance', value: metrics.progressiveDistance, decimals: 1 },
      { label: 'Final Third Entries', value: metrics.finalThirdEntries },
      { label: 'Possession Touches', value: metrics.possessionTouches },
      { label: 'Scoring Touches', value: metrics.scoringPossessionTouches },
      { label: 'Pressure Relief %', value: metrics.possessionTouches ? (1 - (metrics.pressuredPossessionTouches || 0) / metrics.possessionTouches) * 100 : 0, decimals: 1, suffix: '%' },
      { label: 'Forced Turnovers', value: metrics.forcedTurnovers },
      { label: 'Recoveries', value: metrics.recoveries },
      { label: 'Final 3rd Recoveries', value: metrics.finalThirdRecoveries },
      { label: 'Counter Runs', value: metrics.counterRuns },
      { label: 'Turnovers Committed', value: metrics.turnoversCommitted },
      { label: 'Dangerous Turnovers', value: metrics.dangerousTurnovers },
      { label: 'Sweeper Actions', value: metrics.sweeperActions }
    ];
    if (possessionContext) {
      detail.push(
        { label: 'Scoring Possessions', value: possessionContext.scoring },
        { label: 'Turnover Possessions', value: possessionContext.turnovers },
        { label: 'Possession Starts', value: possessionContext.starts },
        { label: 'Possession Touches (Timeline)', value: possessionContext.touches }
      );
    }
    return detail;
  }, [selectedSummary, selectedData, possessionContext]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="haxball-rating-player-title"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'radial-gradient(circle at top left, #111731 0%, #070b1d 60%, #05060d 100%)',
          color: '#f1f5f9',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 32px 70px rgba(4,7,24,0.65)',
          overflow: 'hidden',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogContent sx={{ position: 'relative', pt: 3, px: { xs: 2.5, sm: 3.5 }, pb: 3.5 }}>
        <Button onClick={onClose} sx={{ position: 'absolute', top: 12, right: 12, color: '#cbd5e1' }}>
          Close
        </Button>
        {selectedSummary && selectedData ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              pt: { xs: 2.5, sm: 3 },
              borderRadius: '18px',
              color: '#f1f5f9',
              background: 'linear-gradient(150deg, rgba(255,255,255,0.08) 0%, rgba(12,16,32,0.92) 70%)',
              border: '1px solid rgba(255,255,255,0.12)'
            }}
          >
            <Typography id="haxball-rating-player-title" variant="h6" sx={{ fontWeight: 800, pr: 7 }}>
              {selectedSummary.nick}
            </Typography>
            <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 0.5 }}>
              Role: {selectedSummary.role?.toUpperCase() || 'N/A'} · Team: {selectedSummary.team?.toUpperCase() || 'N/A'}
            </Typography>
            <Typography variant="h4" sx={{ color: '#f8fafc', fontWeight: 800, mt: 1.5, mb: 2 }}>
              {formatRating(selectedData.rating)}<Typography component="span" variant="body2" sx={{ ml: 0.5, color: '#cbd5e1' }}>/10</Typography>
            </Typography>
            <Grid container spacing={1.5}>
              {playerDetailEntries.map((entry) => (
                <Grid item xs={6} sm={4} key={selectedSummary.nick + '-' + entry.label}>
                  <Typography variant="caption" sx={{ display: 'block', color: '#a8b9d8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.7 }}>
                    {entry.label}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: entry.contribution && entry.value > 0 ? '#6ee7b7' : entry.contribution && entry.value < 0 ? '#fda4af' : '#f1f5f9' }}>
                    {formatValue(entry)}
                  </Typography>
                </Grid>
              ))}
            </Grid>
            <div className="rating-dialog-insight">
              <div className="rating-dialog-insight-heading">
                <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 800 }}>Player match evidence</Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>Select a timeline item to jump to that replay frame.</Typography>
              </div>
              <PlayerInsightContent
                match={match}
                player={selectedSummary.nick}
                rating={selectedData}
                onSeek={(tick) => { seekReplayEvidence(tick); onClose?.(); }}
                showSummary={false}
              />
            </div>
          </Paper>
        ) : (
          <Box sx={{ py: 5, color: '#cbd5e1' }}>No rating selected.</Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
